import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Save, X, Plus, Edit, Trash2, BookOpen, FileText, Image as ImageIcon,
  Video, ChevronDown, ChevronRight, Loader2, Code
} from "lucide-react"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { useModules } from "../../hooks/useModules"
import { useLessons } from "../../hooks/useLessons"
import { useBlocks } from "../../hooks/useBlocks"
import { useCourseCreation } from "../../hooks/useCourseCreation"
import { useCourseManagement } from "../../hooks/useCourseManagement"
import { useCategories } from "../../hooks/useCategories"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader } from "../../components/ui/card"
import ContentBlockModal from "../../components/ContentBlockModal"
import ImageUpload from "../../components/ImageUpload"
import { logger } from "../../utils/logger"
import { showSuccessToast, showErrorToast } from "../../utils/toast"
import { api } from "../../utils/apiComponent/apiClient"
import { prepareCourseData } from "../../utils/courseFormData"

export default function EditCoursePage() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()
  
  const { categories } = useCategories()
  const { createModule, createLesson, createBlock, createQuiz } = useCourseCreation()
  const { updateCourse, updateModule, deleteModule, updateLesson, deleteLesson, updateBlock, deleteBlock } = useCourseManagement()

  // Fetch course data
  const {
    data: course,
    loading: courseLoading,
    error: courseError,
    execute: refetchCourse
  } = useGet(`${endpoints.getCourse(courseId)}`)

  // Fetch modules
  const {
    modules,
    loading: modulesLoading,
    error: modulesError,
    refetch: refetchModules
  } = useModules(courseId)

  // State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    price: 0,
    categoryId: '',
    level: 'BEGINNER',
    language: 'Français',
    currency: 'XOF',
    status: 'DRAFT',
    isFree: false,
    isFeatured: false,
    isPublished: false,
    maxStudents: 100,
    certificate: false,
    accessDurationDays: 90,
    isUnlimitedAccess: false,
    duration: 0,
    originalPrice: 0
  })

  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [expandedModules, setExpandedModules] = useState({})
  const [expandedLessons, setExpandedLessons] = useState({})
  const [expandedBlocks, setExpandedBlocks] = useState({})
  const [selectedModule, setSelectedModule] = useState(null)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [lessonModalOpen, setLessonModalOpen] = useState(false)
  const [editingLesson, setEditingLesson] = useState(null)
  const [blockModalOpen, setBlockModalOpen] = useState(false)
  const [blockModalType, setBlockModalType] = useState('TEXT')
  const [editingBlock, setEditingBlock] = useState(null)
  // moduleModalOpen et editingModule supprimés - redirection vers /editor pour créer/éditer des modules
  const [quizModalOpen, setQuizModalOpen] = useState(false)
  const [editingQuiz, setEditingQuiz] = useState(null)

  // Initialize form data when course loads
  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        description: course.description || '',
        shortDescription: course.shortDescription || '',
        price: course.price ? parseInt(course.price) : 0,
        categoryId: course.categoryId || '',
        level: course.level || 'BEGINNER',
        language: course.language || 'Français',
        currency: course.currency || 'XOF',
        status: course.status || 'DRAFT',
        isFree: course.isFree || false,
        isFeatured: course.isFeatured || false,
        isPublished: course.isPublished || false,
        maxStudents: course.maxStudents || 100,
        certificate: course.certificate || false,
        accessDurationDays: course.accessDurationDays || 90,
        isUnlimitedAccess: course.accessDurationDays === null,
        duration: course.duration || 0,
        originalPrice: course.originalPrice ? parseInt(course.originalPrice) : 0
      })
    }
  }, [course])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }))
  }

  const handleSaveCourse = async () => {
    setSaving(true)
    try {
      const { isUnlimitedAccess, ...dataToSend } = formData
      const courseData = {
        ...dataToSend,
        thumbnailFile,
        accessDurationDays: isUnlimitedAccess ? null : formData.accessDurationDays
      }

      const requestData = prepareCourseData(courseData)
      await api.patch(endpoints.updateCourse(courseId), requestData)

      logger.info('Course updated successfully', { courseId })
      showSuccessToast({ message: "Cours mis à jour avec succès !" })
      refetchCourse()
    } catch (err) {
      logger.error('Error updating course', err, { courseId })
      showErrorToast({ message: err?.message || "Erreur lors de la mise à jour du cours" })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteCourse = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce cours ? Cette action est irréversible.')) {
      return
    }
    
    try {
      await api.delete(endpoints.deleteCourse(courseId))
      logger.info('Course deleted successfully', { courseId })
      showSuccessToast({ message: "Cours supprimé avec succès !" })
      navigate('/admin/courses')
    } catch (err) {
      logger.error('Error deleting course', err, { courseId })
      showErrorToast({ message: err?.message || "Erreur lors de la suppression du cours" })
    }
  }

  // Module management
  const handleCreateModule = () => {
    // Rediriger vers l'éditeur avancé pour créer un module
    navigate(`/admin/courses/${courseId}/editor`)
  }

  const handleEditModule = (module) => {
    // Rediriger vers l'éditeur avancé pour éditer le module avec le moduleId en paramètre
    navigate(`/admin/courses/${courseId}/editor?moduleId=${module.id}`)
  }

  // handleSaveModule supprimé - redirection vers /editor pour créer/éditer des modules

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce module ? Cette action supprimera également tous les blocs associés.')) {
      return
    }
    
    try {
      console.log('[EditCoursePage] Suppression du module:', moduleId)
      const response = await api.delete(endpoints.deleteModule(moduleId))
      console.log('[EditCoursePage] Réponse de suppression:', response)
      
      showSuccessToast({ message: "Module supprimé avec succès !" })
      
      // Attendre un peu avant de rafraîchir pour que le backend enregistre la suppression
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir les modules
      await refetchModules()
      console.log('[EditCoursePage] Modules rafraîchis après suppression')
    } catch (err) {
      console.error('[EditCoursePage] Erreur suppression module:', err)
      logger.error('Error deleting module', err)
      showErrorToast({ message: err?.message || "Erreur lors de la suppression du module" })
    }
  }

  // Lesson management
  const handleCreateLesson = (moduleId) => {
    setSelectedModule(moduleId)
    setEditingLesson(null)
    setLessonModalOpen(true)
  }

  const handleEditLesson = (lesson, moduleId) => {
    setSelectedModule(moduleId)
    setEditingLesson(lesson)
    setLessonModalOpen(true)
  }

  const handleSaveLesson = async (lessonData) => {
    try {
      if (editingLesson) {
        await updateLesson(editingLesson.id, lessonData)
        showSuccessToast({ message: "Chapitre mise à jour avec succès !" })
      } else {
        await createLesson(lessonData, selectedModule)
        showSuccessToast({ message: "Chapitre créé avec succès !" })
      }
      
      setLessonModalOpen(false)
      
      // Attendre un peu pour que le backend enregistre la leçon
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir les modules (qui incluent les leçons)
      await refetchModules()
    } catch (err) {
      logger.error('Error saving lesson', err)
      showErrorToast({ message: err?.message || "Erreur lors de l'enregistrement du chapitre" })
    }
  }

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce module ? Cette action supprimera également tous les blocs associés.')) {
      return
    }
    
    try {
      console.log('[EditCoursePage] Suppression du chapitre:', lessonId)
      await api.delete(endpoints.deleteModuleLesson(lessonId))
      showSuccessToast({ message: "Chapitre supprimé avec succès !" })
      
      // Attendre un peu pour que le backend enregistre la suppression
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir les modules
      await refetchModules()
    } catch (err) {
      console.error('[EditCoursePage] Erreur suppression chapitre:', err)
      logger.error('Error deleting lesson', err)
      showErrorToast({ message: err?.message || "Erreur lors de la suppression du chapitre" })
    }
  }

  // Block management
  const handleCreateBlock = (lessonId, blockType) => {
    setSelectedLesson(lessonId)
    setBlockModalType(blockType)
    setEditingBlock(null)
    setBlockModalOpen(true)
  }

  const handleEditBlock = (block, lessonId) => {
    setSelectedLesson(lessonId)
    setBlockModalType(block.type)
    setEditingBlock(block)
    setBlockModalOpen(true)
  }

  const handleSaveBlock = async (blockData) => {
    try {
      // ContentBlockModal passe déjà le content formaté selon le type
      const blockPayload = {
        title: blockData.title,
        description: blockData.description || '',
        type: blockModalType,
        content: blockData.content, // Déjà formaté par ContentBlockModal
        order: blockData.order || 1,
        duration: blockData.duration || 0
      }

      if (editingBlock) {
        await updateBlock(editingBlock.id, blockPayload)
        showSuccessToast({ message: "Bloc mis à jour avec succès !" })
      } else {
        await createBlock(blockPayload, selectedLesson)
        showSuccessToast({ message: "Bloc créé avec succès !" })
      }
      
      setBlockModalOpen(false)
      
      // Attendre un peu pour que le backend enregistre le bloc
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir les modules (qui incluent les blocs)
      await refetchModules()
      
      console.log('[EditCoursePage] Modules rafraîchis après création/modification de bloc')
    } catch (err) {
      logger.error('Error saving block', err)
      showErrorToast({ message: err?.message || "Erreur lors de l'enregistrement du bloc" })
    }
  }

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce bloc ?')) {
      return
    }
    
    try {
      console.log('[EditCoursePage] Suppression du bloc:', blockId)
      await api.delete(endpoints.deleteModuleBlock(blockId))
      showSuccessToast({ message: "Bloc supprimé avec succès !" })
      
      // Attendre un peu pour que le backend enregistre la suppression
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Rafraîchir les modules (qui incluent les blocs)
      await refetchModules()
      
      console.log('[EditCoursePage] Modules rafraîchis après suppression de bloc')
    } catch (err) {
      console.error('[EditCoursePage] Erreur suppression bloc:', err)
      logger.error('Error deleting block', err)
      showErrorToast({ message: err?.message || "Erreur lors de la suppression du bloc" })
    }
  }

  // Quiz management
  const handleCreateQuiz = (moduleId) => {
    setSelectedModule(moduleId)
    setEditingQuiz(null)
    setQuizModalOpen(true)
  }

  const handleEditQuiz = (quiz, moduleId) => {
    setSelectedModule(moduleId)
    setEditingQuiz(quiz)
    setQuizModalOpen(true)
  }

  const handleSaveQuiz = async (quizData) => {
    try {
      if (editingQuiz) {
        await api.patch(endpoints.updateQuiz(editingQuiz.id), quizData)
        showSuccessToast({ message: "Quiz mis à jour avec succès !" })
      } else {
        await createQuiz({ ...quizData, order: 1 }, selectedModule)
        showSuccessToast({ message: "Quiz créé avec succès !" })
      }
      setQuizModalOpen(false)
      refetchModules()
    } catch (err) {
      logger.error('Error saving quiz', err)
      showErrorToast({ message: err?.message || "Erreur lors de l'enregistrement du quiz" })
    }
  }

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce quiz ?')) {
      return
    }
    
    try {
      await api.delete(endpoints.deleteQuiz(quizId))
      showSuccessToast({ message: "Quiz supprimé avec succès !" })
      refetchModules()
    } catch (err) {
      logger.error('Error deleting quiz', err)
      showErrorToast({ message: err?.message || "Erreur lors de la suppression du quiz" })
    }
  }

  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }))
  }

  const toggleLessonExpansion = (lessonId) => {
    setExpandedLessons(prev => ({
      ...prev,
      [lessonId]: !prev[lessonId]
    }))
  }

  if (courseLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
            <p className="text-gray-600">Chargement du cours...</p>
          </div>
        </div>
      </div>
    )
  }

  if (courseError || !course) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
            <p className="text-gray-800">Erreur lors du chargement du cours</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title={`Éditer: ${course.title}`} 
          subtitle="Modifier les informations du cours, modules, blocs et quiz"
        />
        
        {/* Link to Advanced Editor */}
        <div className="mb-6">
          <Button
            onClick={() => navigate(`/admin/courses/${courseId}/editor`)}
            className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
          >
            <Code className="w-4 h-4 mr-2" />
            Ouvrir l'éditeur avancé
          </Button>
        </div>
        
        {/* Course Info Form */}
        <Card className="mb-6">
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Informations du cours
            </h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.displayName || cat.translatedName || cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prix (centimes)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                >
                  <option value="DRAFT">Brouillon</option>
                  <option value="PUBLISHED">Publié</option>
                  <option value="ARCHIVED">Archivé</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Durée d'accès (jours)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    name="accessDurationDays"
                    value={formData.accessDurationDays}
                    onChange={handleChange}
                    min="1"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    disabled={formData.isUnlimitedAccess}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="isUnlimitedAccess"
                      checked={formData.isUnlimitedAccess}
                      onChange={handleChange}
                      className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                    />
                    <label className="text-sm text-gray-700">Illimité</label>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {formData.isUnlimitedAccess
                    ? "L'accès au cours est illimité (accessExpiresAt sera null)"
                    : "L'accès expirera après le nombre de jours spécifié"}
                </p>
              </div>
            </div>
            
            {/* Images */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>
                <ImageUpload
                  onImageSelect={(file) => setThumbnailFile(file)}
                  currentImage={course.thumbnail}
                  placeholder="Cliquez pour changer l'image miniature"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={handleDeleteCourse}
                className="text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer le cours
              </Button>
              <Button
                onClick={handleSaveCourse}
                disabled={saving}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder les modifications
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Modules Management */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Modules ({modules.length})</h2>
              <Button
                onClick={handleCreateModule}
                className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un module
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {modulesLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#141835] mx-auto mb-2" />
                <p className="text-gray-600">Chargement des modules...</p>
              </div>
            ) : modules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucun module pour ce cours. Cliquez sur "Ajouter un module" pour commencer.
              </div>
            ) : (
              <div className="space-y-4">
                {modules.map((module) => {
                  const isExpanded = expandedModules[module.id]
                  return (
                    <ModuleCard
                      key={module.id}
                      module={module}
                      courseId={courseId}
                      isExpanded={isExpanded}
                      onToggle={() => toggleModuleExpansion(module.id)}
                      onEdit={() => handleEditModule(module)}
                      onDelete={() => handleDeleteModule(module.id)}
                      onCreateLesson={() => handleCreateLesson(module.id)}
                      onEditLesson={(lesson) => handleEditLesson(lesson, module.id)}
                      onDeleteLesson={handleDeleteLesson}
                      onCreateQuiz={() => handleCreateQuiz(module.id)}
                      onEditQuiz={(quiz) => handleEditQuiz(quiz, module.id)}
                      onDeleteQuiz={handleDeleteQuiz}
                      expandedLessons={expandedLessons}
                      onToggleLesson={toggleLessonExpansion}
                      onCreateBlock={handleCreateBlock}
                      onEditBlock={handleEditBlock}
                      onDeleteBlock={handleDeleteBlock}
                    />
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Module Modal - Supprimé : redirection vers /editor pour créer/éditer des modules */}

        {/* Lesson Modal */}
        {lessonModalOpen && (
          <LessonModal
            lesson={editingLesson}
            onClose={() => setLessonModalOpen(false)}
            onSave={handleSaveLesson}
          />
        )}

        {/* Block Modal */}
        {blockModalOpen && (
          <ContentBlockModal
            isOpen={blockModalOpen}
            onClose={() => setBlockModalOpen(false)}
            blockType={blockModalType}
            block={editingBlock}
            onSave={handleSaveBlock}
          />
        )}

        {/* Quiz Modal */}
        {quizModalOpen && (
          <QuizModal
            quiz={editingQuiz}
            onClose={() => setQuizModalOpen(false)}
            onSave={handleSaveQuiz}
          />
        )}
      </div>
    </div>
  )
}

// Module Card Component
function ModuleCard({ 
  module, 
  isExpanded, 
  onToggle, 
  onEdit, 
  onDelete,
  onCreateLesson,
  onEditLesson,
  onDeleteLesson,
  onCreateQuiz,
  onEditQuiz,
  onDeleteQuiz,
  expandedLessons,
  onToggleLesson,
  onCreateBlock,
  onEditBlock,
  onDeleteBlock
}) {
  // Utiliser les leçons qui viennent directement des modules (déjà inclus dans la réponse GET /modules)
  // Si les leçons ne sont pas dans module.lessons, faire une requête séparée
  const moduleLessons = module.lessons || []
  const { lessons: fetchedLessons, loading: lessonsLoading, refetch: refetchLessons } = useLessons(moduleLessons.length === 0 && isExpanded ? module.id : null)
  
  // Utiliser les leçons du module s'ils existent, sinon utiliser les leçons récupérées
  const lessons = moduleLessons.length > 0 ? moduleLessons : fetchedLessons
  
  // Rafraîchir les leçons quand le module est étendu (si on utilise useLessons)
  useEffect(() => {
    if (isExpanded && module.id && moduleLessons.length === 0) {
      refetchLessons()
    }
  }, [isExpanded, module.id, moduleLessons.length, refetchLessons])
  
  // Fetch quiz for this module (if any)
  const [quiz, setQuiz] = useState(null)
  
  useEffect(() => {
    // TODO: Fetch quiz for module if needed
    // For now, we'll assume quiz is part of module data
  }, [module])

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader className="cursor-pointer hover:bg-gray-50 relative">
        <div className="flex items-center justify-between w-full" onClick={onToggle}>
          <div className="flex items-center space-x-3 flex-1">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{module.title}</h3>
              <p className="text-sm text-gray-500">{module.description || 'Pas de description'}</p>
            </div>
          </div>
        </div>
        {/* Boutons d'action - positionnés absolument pour être toujours visibles */}
        <div 
          className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2 z-10" 
          onClick={(e) => e.stopPropagation()}
        >
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => { 
              e.stopPropagation(); 
              onEdit(); 
            }}
            className="hover:bg-blue-50 bg-white shadow-sm"
            title="Modifier le module"
          >
            <Edit className="w-4 h-4 mr-1" />
            <span className="text-xs">Modifier</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => { 
              e.stopPropagation(); 
              console.log('[ModuleCard] Clic sur le bouton de suppression du module:', module.id)
              onDelete(); 
            }}
            className="hover:bg-red-50 hover:border-red-300 text-red-600 border-red-300 bg-white shadow-sm"
            title="Supprimer le module"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            <span className="text-xs">Supprimer</span>
          </Button>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          {/* Lessons */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Chapitres ({lessons?.length || 0})</h4>
              <Button size="sm" variant="outline" onClick={onCreateLesson}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un chapitre
              </Button>
            </div>
            
            {lessonsLoading ? (
              <div className="text-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : lessons && lessons.length > 0 ? (
              <div className="space-y-2">
                {lessons.map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isExpanded={expandedLessons[lesson.id]}
                    onToggle={() => onToggleLesson(lesson.id)}
                    onEdit={() => onEditLesson(lesson, module.id)}
                    onDelete={() => onDeleteLesson(lesson.id)}
                    onCreateBlock={(type) => onCreateBlock(lesson.id, type)}
                    onEditBlock={(block) => onEditBlock(block, lesson.id)}
                    onDeleteBlock={onDeleteBlock}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Aucun chapitre pour ce module</p>
            )}
          </div>

          {/* Quiz */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Quiz</h4>
              <Button size="sm" variant="outline" onClick={onCreateQuiz}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un quiz
              </Button>
            </div>
            {/* TODO: Display quiz if exists */}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

// Lesson Card Component
function LessonCard({ 
  lesson, 
  isExpanded, 
  onToggle, 
  onEdit, 
  onDelete,
  onCreateBlock,
  onEditBlock,
  onDeleteBlock
}) {
  // Utiliser les blocs qui viennent directement de la leçon (déjà inclus dans la réponse GET /module-lessons)
  // Si les blocs ne sont pas dans lesson.blocks, faire une requête séparée
  const lessonBlocks = lesson.blocks || []
  const { blocks: fetchedBlocks, loading: blocksLoading, refetch: refetchBlocks } = useBlocks(lessonBlocks.length === 0 && isExpanded ? lesson.id : null)
  
  // Utiliser les blocs de la leçon s'ils existent, sinon utiliser les blocs récupérés
  const blocks = lessonBlocks.length > 0 ? lessonBlocks : fetchedBlocks
  
  // Rafraîchir les blocs quand la leçon est étendue (si on utilise useBlocks)
  useEffect(() => {
    if (isExpanded && lesson.id && lessonBlocks.length === 0) {
      refetchBlocks()
    }
  }, [isExpanded, lesson.id, lessonBlocks.length, refetchBlocks])

  return (
    <Card className="border-l-4 border-l-green-500 ml-4">
      <CardHeader className="cursor-pointer hover:bg-gray-50 relative">
        <div className="flex items-center justify-between w-full" onClick={onToggle}>
          <div className="flex items-center space-x-3 flex-1">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <h4 className="text-md font-semibold text-gray-900">{lesson.title}</h4>
              <p className="text-xs text-gray-500">{lesson.description || 'Pas de description'}</p>
            </div>
          </div>
        </div>
        {/* Boutons d'action */}
        <div 
          className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2 z-10" 
          onClick={(e) => e.stopPropagation()}
        >
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => { 
              e.stopPropagation(); 
              onEdit(); 
            }}
            className="hover:bg-green-50 bg-white shadow-sm"
            title="Modifier le chapitre"
          >
            <Edit className="w-4 h-4 mr-1" />
            <span className="text-xs">Modifier</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={(e) => { 
              e.stopPropagation(); 
              onDelete(); 
            }}
            className="hover:bg-red-50 hover:border-red-300 text-red-600 border-red-300 bg-white shadow-sm"
            title="Supprimer le chapitre"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            <span className="text-xs">Supprimer</span>
          </Button>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          {/* Blocks */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-900 text-sm">Blocs ({blocks?.length || 0})</h5>
              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={() => onCreateBlock('TEXT')}>
                  <FileText className="w-4 h-4 mr-1" />
                  Texte
                </Button>
                <Button size="sm" variant="outline" onClick={() => onCreateBlock('IMAGE')}>
                  <ImageIcon className="w-4 h-4 mr-1" />
                  Image
                </Button>
                <Button size="sm" variant="outline" onClick={() => onCreateBlock('VIDEO')}>
                  <Video className="w-4 h-4 mr-1" />
                  Vidéo
                </Button>
              </div>
            </div>
            
            {blocksLoading ? (
              <div className="text-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : blocks && blocks.length > 0 ? (
              <div className="space-y-2">
                {blocks.map((block) => (
                  <div key={block.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center space-x-2">
                      {block.type === 'TEXT' && <FileText className="w-4 h-4 text-blue-500" />}
                      {block.type === 'IMAGE' && <ImageIcon className="w-4 h-4 text-green-500" />}
                      {block.type === 'VIDEO' && <Video className="w-4 h-4 text-purple-500" />}
                      <span className="text-sm font-medium">{block.title}</span>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="ghost" onClick={() => onEditBlock(block)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDeleteBlock(block.id)}>
                        <Trash2 className="w-4 h-4 text-gray-700" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Aucun bloc pour ce chapitre</p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

// Lesson Modal Component
function LessonModal({ lesson, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: lesson?.title || '',
    description: lesson?.description || '',
    order: lesson?.order || 1,
    duration: lesson?.duration || 0
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{lesson ? 'Modifier le chapitre' : 'Nouveaux chapitre'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ordre</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (minutes)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
              <Save className="w-4 h-4 mr-2" />
              {lesson ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Module Modal Component
function ModuleModal({ module, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: module?.title || '',
    description: module?.description || '',
    order: module?.order || 1,
    duration: module?.duration || 0
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{module ? 'Modifier le module' : 'Nouveau module'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ordre</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (secondes)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
              <Save className="w-4 h-4 mr-2" />
              {module ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Quiz Modal Component
function QuizModal({ quiz, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: quiz?.title || '',
    description: quiz?.description || '',
    instructions: quiz?.instructions || 'Répondez à toutes les questions.',
    timeLimit: quiz?.timeLimit || 30,
    passingScore: quiz?.passingScore || 100,
    maxAttempts: quiz?.maxAttempts || 3
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{quiz ? 'Modifier le quiz' : 'Nouveau quiz'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Temps limite (min)</label>
              <input
                type="number"
                value={formData.timeLimit}
                onChange={(e) => setFormData(prev => ({ ...prev, timeLimit: parseInt(e.target.value) || 30 }))}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Score min (%)</label>
              <input
                type="number"
                value={formData.passingScore}
                onChange={(e) => setFormData(prev => ({ ...prev, passingScore: parseInt(e.target.value) || 70 }))}
                min="0"
                max="100"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tentatives max</label>
              <input
                type="number"
                value={formData.maxAttempts}
                onChange={(e) => setFormData(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 3 }))}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
              <Save className="w-4 h-4 mr-2" />
              {quiz ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
